<script setup lang="ts">
import { createReusableTemplate } from '@vueuse/core';

import { AuctionState, ComputedState, DirectSaleComputedState, SellingMethod, ValueAddedServiceCode, type ValueAddedServiceDto } from '~/apiClient';
import type { VasPowertrainWarrantyDuration } from '~/components/Vas/VasPowertrainWarrantyFormField.vue';
import { INJECTION_KEYS } from '~/constants/injectionKeys';
import { type SaleItemEntity } from '~/types/sale.type';

type ExtendedVas = ValueAddedServiceDto & { showAsPurchasable: boolean; title: string; tooltipText: string };

// PROPS
const { sale } = defineProps({
    sale: { type: Object as PropType<SaleItemEntity>, required: true },
});

// COMPOSABLES
const { tt, tn, formatUnits } = useTypedI18n();
const { isLoggedIn } = useAuthUtils();
const { openAuthModal } = useModals();
const { title: provisionalTitle, tooltipText: provisionalTooltipText } = useProvisionalLicensePlateLocale(
    () => sale.mainAsset.registrationDocumentCountry
);
const [DefineVasTitleTemplate, ReuseVasTitleTemplate] = createReusableTemplate<{ vas: ExtendedVas }>();
const [DefineVasPriceTemplate, ReuseVasPriceTemplate] = createReusableTemplate<{ vas: ExtendedVas; price?: number }>();

// DATA
const isLogisticsModalOpen = ref(false);
const logisticsPrice = ref<number>();
const logisticsFullAddress = ref<string>();

const isPowerTrainWarrantyModalOpen = ref(false);
const powerTrainWarrantyPrice = ref<number>();
const powerTrainWarrantyDuration = ref<VasPowertrainWarrantyDuration>();

const valueAddedServices = inject(INJECTION_KEYS.VALUE_ADDED_SERVICES);

// Avoid show VAS for sold Sale (see BSD-2193 for further details)
// It can lead to ambiguity between the VAS that are available and those that have actually been purchased.
const showVas = computed(() => {
    if (sale.sellingMethod === SellingMethod.Auction) {
        return [AuctionState.Pending, AuctionState.Running].includes(sale.state) || sale.computedState === ComputedState.UnderNegotiation;
    }

    if (sale.sellingMethod === SellingMethod.DirectSale) {
        return sale.computedState === DirectSaleComputedState.NoActiveOrder;
    }

    return false;
});

const extendedVas = computed<ExtendedVas[]>(
    () =>
        sortVasList(valueAddedServices?.value)
            ?.filter(vas => vas.enabled)
            .map(vas => {
                const optimisticGuestPurchase = vas.isUserInformationRequired && !isLoggedIn.value;

                const isProvisionalLicensePlate = vas.valueAddedServiceCode === ValueAddedServiceCode.ProvisionalLicensePlate;

                const title =
                    isProvisionalLicensePlate && provisionalTitle.value ? provisionalTitle.value : tt(`vas.${vas.valueAddedServiceCode}.title`);
                const tooltipText =
                    isProvisionalLicensePlate && provisionalTooltipText.value
                        ? provisionalTooltipText.value
                        : tt(`sdp.vas.tooltip.${vas.valueAddedServiceCode}`);

                return { ...vas, showAsPurchasable: vas.purchasable || optimisticGuestPurchase, title, tooltipText };
            }) ?? []
);

const hasLogistics = computed(
    () => !!extendedVas.value.find(vas => vas.valueAddedServiceCode === ValueAddedServiceCode.LogisticsService && vas.showAsPurchasable)
);

const hasPowertrainWarranty = computed(
    () => !!extendedVas.value.find(vas => vas.valueAddedServiceCode === ValueAddedServiceCode.PowertrainWarranty && vas.showAsPurchasable)
);

// METHODS
const getLabelClass = (vas: ExtendedVas): string => (!vas.showAsPurchasable ? 'text-neutral-400' : 'text-neutral-700');

const getValueClass = (vas: ExtendedVas): string => (!vas.showAsPurchasable ? 'text-neutral-500 italic' : 'text-primary font-semibold');

const onSelectedAddress = (price?: number, fullAddress?: string): void => {
    logisticsPrice.value = price;
    logisticsFullAddress.value = fullAddress;
};

const onSelectPowertrainWarranty = (price?: number, duration?: VasPowertrainWarrantyDuration): void => {
    powerTrainWarrantyPrice.value = price;
    powerTrainWarrantyDuration.value = duration;
};
</script>

<template>
    <DefineVasTitleTemplate v-slot="{ $slots, vas }">
        <span class="flex min-w-0 items-center gap-1">
            <BaseTooltip :text="vas.tooltipText">
                <UIcon class="size-4 shrink-0" name="lucide:info" />
            </BaseTooltip>
            <span class="truncate" :class="getLabelClass(vas)">
                <component :is="$slots.default" v-if="!!$slots.default" />
                <span v-else>{{ vas.title }}</span>
                <span v-if="!isLoggedIn" class="ms-0.5">*</span>
            </span>
        </span>
    </DefineVasTitleTemplate>
    <DefineVasPriceTemplate v-slot="{ $slots, vas, price }">
        <span v-if="!vas.showAsPurchasable" class="shrink-0" :class="getValueClass(vas)">{{ tt('sdp.vas.notAvailable') }}</span>
        <template v-else>
            <span v-if="price" class="shrink-0" :class="getValueClass(vas)">{{ tn(price, 'currency') }}</span>
            <span v-else-if="vas.totalPrice" class="shrink-0" :class="getValueClass(vas)">{{ tn(vas.totalPrice, 'currency') }}</span>
            <component :is="$slots.default" v-else />
        </template>
    </DefineVasPriceTemplate>
    <div v-if="extendedVas.length && showVas" class="p-4 lg:p-6">
        <p class="text-sm font-semibold">{{ tt('sdp.vas.title') }}</p>

        <div v-for="vas of extendedVas" :key="vas.valueAddedServiceCode" class="text-sm leading-6">
            <template v-if="vas.valueAddedServiceCode === ValueAddedServiceCode.LogisticsService">
                <div class="flex justify-between gap-2">
                    <ReuseVasTitleTemplate :vas="vas" />

                    <ReuseVasPriceTemplate :vas="vas" :price="logisticsPrice">
                        <UButton
                            variant="link"
                            size="xs"
                            :label="tt('sdp.vas.selection.compute')"
                            class="p-0 text-sm"
                            trailing-icon="lucide:square-pen"
                            @click="isLogisticsModalOpen = true"
                        />
                    </ReuseVasPriceTemplate>
                </div>

                <div v-if="logisticsFullAddress" class="flex justify-between gap-6">
                    <span class="text-primary truncate text-xs leading-6 italic">{{ logisticsFullAddress }}</span>

                    <UButton
                        variant="link"
                        size="xs"
                        :label="tt('sdp.vas.selection.update')"
                        class="p-0 text-sm"
                        trailing-icon="lucide:square-pen"
                        @click="isLogisticsModalOpen = true"
                    />
                </div>
            </template>
            <template v-else-if="vas.valueAddedServiceCode === ValueAddedServiceCode.PowertrainWarranty">
                <div class="flex justify-between gap-2">
                    <ReuseVasTitleTemplate :vas="vas">
                        <BaseI18nT keypath="vas.PowertrainWarranty.highlightedTitle.base" tag="span">
                            <template #highlight>
                                <span class="text-primary"> {{ tt('vas.PowertrainWarranty.highlightedTitle.highlight') }} </span>
                            </template>
                        </BaseI18nT>
                    </ReuseVasTitleTemplate>

                    <ReuseVasPriceTemplate :vas="vas" :price="powerTrainWarrantyPrice">
                        <UButton
                            variant="link"
                            size="xs"
                            :label="tt('sdp.vas.selection.compute')"
                            class="p-0 text-sm"
                            trailing-icon="lucide:square-pen"
                            @click="isPowerTrainWarrantyModalOpen = true"
                        />
                    </ReuseVasPriceTemplate>
                </div>

                <div v-if="powerTrainWarrantyDuration" class="flex justify-between gap-6">
                    <span class="truncate text-xs leading-6 italic">{{
                        tt('vas.PowertrainWarranty.duration.itemLabel', {
                            months: powerTrainWarrantyDuration.durationMonths,
                            km: formatUnits.km(powerTrainWarrantyDuration.mileageLimitKm),
                        })
                    }}</span>

                    <UButton
                        variant="link"
                        size="xs"
                        :label="tt('sdp.vas.selection.update')"
                        class="p-0 text-sm"
                        trailing-icon="lucide:square-pen"
                        @click="isPowerTrainWarrantyModalOpen = true"
                    />
                </div>
            </template>
            <template v-else>
                <div class="flex justify-between gap-2">
                    <ReuseVasTitleTemplate v-if="vas.valueAddedServiceCode === ValueAddedServiceCode.FleequidCare" :vas="vas">
                        <BaseI18nT keypath="vas.FleequidCare.highlightedTitle.base" tag="span">
                            <template #highlight>
                                <span class="text-primary"> {{ tt('vas.FleequidCare.highlightedTitle.highlight') }} </span>
                            </template>
                        </BaseI18nT>
                    </ReuseVasTitleTemplate>
                    <ReuseVasTitleTemplate v-else :vas="vas" />

                    <ReuseVasPriceTemplate :vas="vas" />
                </div>
            </template>
        </div>

        <p v-if="!isLoggedIn" class="text-xs">
            <span class="me-0.5">*</span>
            <BaseI18nT tag="span" keypath="sdp.vas.guestDisclaimer">
                <template #loginOrSignup>
                    <UButton class="p-0" size="sm" variant="link" :label="tt('sdp.vas.loginOrSignup')" @click="openAuthModal()" />
                </template>
            </BaseI18nT>
        </p>
    </div>

    <SdpSalePanelVasLogisticsModal v-if="hasLogistics" v-model:open="isLogisticsModalOpen" :sale="sale" @select-address="onSelectedAddress" />
    <SdpSalePanelVasPowertrainWarrantyModal
        v-if="hasPowertrainWarranty"
        v-model:open="isPowerTrainWarrantyModalOpen"
        @select-powertrain-warranty="onSelectPowertrainWarranty"
    />
</template>
